import { Checkbox } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import Button from 'Components/Base/Button'
import Typography from 'Components/Base/Typography'
import { CustomPagination } from 'Pages/DashboardPage/components/CustomPagination'
import { bodyCompScaleColumns, scaleColumns } from 'Pages/DashboardPage/constants'
import { theme } from 'Styles/styledTheme'
import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { EditModalSettings, ScaleReading, ScaleRow } from 'types/Dashboards'
import { mapBodyCompScaleReadingsToTableRows } from 'utilities/dashboardUtilities/mapBodyCompReadingsToTableRows'
import { mapScaleReadingsToTableRows } from 'utilities/dashboardUtilities/mapScaleReadingsToTableRows'
import { sortReadingsByDate } from '../../../DashboardGlucosePage/dateFunctions'
import { CustomScaleToolbar } from '../CustomScaleToolbar'
import MobileScaleDataGrid from '../MobileScaleDataGrid'

type Props = {
  readings: ScaleReading[]
  isMobile: boolean
  hasBodyComposition: boolean
}

export default function ReadingDataGrid({ readings, isMobile, hasBodyComposition }: Props) {
  const [tableRows, setTableRows] = useState<ScaleRow[] | undefined>([])
  const [editModalSettings, setEditModalSettings] = useState<EditModalSettings>({
    isOpen: false,
  })
  const [columns, setColumns] = useState(hasBodyComposition ? bodyCompScaleColumns : scaleColumns)
  const [editorOpen, setEditorOpen] = useState(false)
  const toggleableFields = ['weight', 'change', 'bodyFatPercent', 'musclePercent', 'waterPercent', 'visceralFat', 'vascularAge']

  useEffect(() => {
    let readingsCopy = [...readings]
    readingsCopy = sortReadingsByDate(readingsCopy)

    if (hasBodyComposition) {
      const bodyCompMappedRows = mapBodyCompScaleReadingsToTableRows(readingsCopy, false)
      setTableRows(bodyCompMappedRows?.reverse())
    } else {
      const mappedRows = mapScaleReadingsToTableRows(readingsCopy, false)
      setTableRows(mappedRows?.reverse())
    }
  }, [readings, hasBodyComposition])

  const Toolbar = () => <CustomScaleToolbar isMobile={isMobile} hasBodyComposition={hasBodyComposition} onEditClick={() => setEditorOpen(true)} />

  if (!readings || readings.length < 0) return <></>

  return (
    <>
      <div style={isMobile ? { marginBottom: 50, marginTop: 50 } : { marginBottom: 50 }} className="dashboard-grid-container">
        {tableRows && readings && (
          <div style={{ width: '100%', fontFamily: 'Raleway' }}>
            <DataGrid
              rows={tableRows}
              columns={[...columns, { field: 'readingTime', sortable: false }]}
              headerHeight={!hasBodyComposition && isMobile ? 10 : 70}
              style={isMobile ? { height: hasBodyComposition ? 120 : 105, overflow: 'hidden' } : { height: tableRows.length < 15 ? 'auto' : 500 }}
              disableColumnMenu
              disableSelectionOnClick
              sortingOrder={['asc', 'desc']}
              autoHeight={tableRows.length < 15}
              onCellClick={(cell) => {
                if (cell.field === 'edit') {
                  setEditModalSettings({
                    ...editModalSettings,
                    isOpen: true,
                    scaleReading: readings.find((x) => x.patientReadingPId.toString() === cell.row.readingId),
                  })
                }
              }}
              components={{
                Pagination: () => (isMobile ? <></> : <CustomPagination />),
                Toolbar: Toolbar,
              }}
            />
            {isMobile ? <MobileScaleDataGrid tableRows={tableRows} columns={columns} hasBodyComposition={hasBodyComposition} /> : <></>}
          </div>
        )}
      </div>
      <Modal show={editorOpen} onHide={() => setEditorOpen(false)} dialogClassName="auto-width-modal" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Columns</Modal.Title>
        </Modal.Header>
        <div style={{ padding: 20 }}>
          {columns
            .filter((col) => toggleableFields.includes(col.field))
            .map((col) => (
              <div key={col.field} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Checkbox
                  checked={!col.hide}
                  onChange={(value) => {
                    const updated = columns.map((c) => (c.field === col.field ? { ...c, hide: !value.target.checked } : c))
                    setColumns(updated)
                  }}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      stroke: theme.colors.primary1,
                      strokeWidth: 2,
                    },
                    '&.Mui-checked .MuiSvgIcon-root': {
                      fill: theme.colors.primary1,
                      stroke: theme.colors.primary1,
                    },
                  }}
                />
                <Typography>{col.headerName ?? col.field}</Typography>
              </div>
            ))}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 5 }}>
            <Button text="Save" onClick={() => setEditorOpen(false)} />
          </div>
        </div>
      </Modal>
    </>
  )
}
